import { Box, Paper, Typography } from '@mui/material'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Psychology from '@mui/icons-material/Psychology'
import PersonSearch from '@mui/icons-material/PersonSearch'
import HelpOutline from '@mui/icons-material/HelpOutline'
import Block from '@mui/icons-material/Block'
import { useNavigate, useSearchParams } from 'react-router-dom'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import TherapistApplicantsListing from 'src/supportPortal/views/therapists/TherapistApplicantsListing'

/**
 * Phase 12 — Applicants picker (4 tiles) when no ?type query param. When ?type is set,
 * renders the existing TherapistApplicantsListing for that bucket so each tile keeps the
 * full listing UX (Approve / Review in onboarding / chips). Bookmarkable via the URL.
 */
const TILES = [
  {
    id: 'doctor',
    label: 'Doctor Applicants',
    description: 'Pending-KYC doctor applicants waiting for clinic admin review.',
    Icon: LocalHospital,
    gradient: 'linear-gradient(135deg, rgba(0, 43, 107, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  },
  {
    id: 'therapist',
    label: 'Therapist Applicants',
    description: 'Pending-KYC therapist applicants (psychotherapy + nutrition).',
    Icon: Psychology,
    gradient: 'linear-gradient(135deg, rgba(216, 101, 0, 0.95) 0%, rgba(194, 65, 12, 0.95) 100%)',
  },
  {
    id: 'screener',
    label: 'Screener Applicants',
    description: 'Pending-KYC screener applicants for target-specialty buckets.',
    Icon: PersonSearch,
    gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(67, 56, 202, 0.95) 100%)',
  },
  {
    id: 'uncategorized',
    label: 'Uncategorized Applicants',
    description: 'Applicants with no recognised practitioner-type tag — triage and approve.',
    Icon: HelpOutline,
    gradient: 'linear-gradient(135deg, rgba(244, 63, 94, 0.95) 0%, rgba(225, 29, 72, 0.95) 100%)',
  },
  {
    // Phase 14c — fifth tile surfaces rejections so the team can read reasons / re-open.
    id: 'rejected',
    label: 'Rejected Applications',
    description: 'Applicants rejected with a reason — review and re-open if circumstances change.',
    Icon: Block,
    gradient: 'linear-gradient(135deg, rgba(75, 85, 99, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)',
  },
]

const TYPE_LABEL = {
  doctor: 'Doctor',
  therapist: 'Therapist',
  screener: 'Screener',
  uncategorized: 'Uncategorized',
  rejected: 'Rejected',
}

export default function TherapistApplicantsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const type = (searchParams.get('type') || '').toLowerCase()

  // No type → render the 4-tile picker.
  if (!type) {
    return (
      <>
        <SupportHeader heading="Applicants" showBackButton />
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Pick the applicant type to triage. Each tile lists pending-KYC submissions of that
            kind with Approve and Review-in-onboarding actions inline.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
            }}
          >
            {TILES.map(({ id, label, description, Icon, gradient }) => (
              <Paper
                key={id}
                elevation={0}
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('type', id)
                  setSearchParams(next, { replace: false })
                }}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  cursor: 'pointer',
                  background: gradient,
                  color: 'white',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  {description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </>
    )
  }

  // Type set → reuse the existing listing (its endpoint already handles each value).
  return (
    <>
      <SupportHeader heading={`${TYPE_LABEL[type] || 'Applicants'} Applicants`} showBackButton />
      <Box sx={{ px: { xs: 1, md: 2 }, py: 1, maxWidth: 1400, mx: 'auto' }}>
        <Typography
          component="button"
          variant="body2"
          onClick={() => {
            const next = new URLSearchParams(searchParams)
            next.delete('type')
            setSearchParams(next, { replace: false })
          }}
          sx={{
            background: 'none',
            border: 'none',
            color: 'primary.main',
            cursor: 'pointer',
            p: 0,
            mb: 1,
          }}
        >
          ← Back to applicant types
        </Typography>
      </Box>
      <TherapistApplicantsListing
        discipline={type}
        disciplineLabel={TYPE_LABEL[type] || ''}
      />
    </>
  )
}
